import { useCallback } from 'react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Level1Data {
  id: string;
  code: string;
  name: string;
}

interface Level2Data {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: 'receita' | 'despesa';
}

interface Level3Data {
  id: string;
  level2Id?: string;
  code?: string;
  name: string;
  type: 'receita' | 'despesa';
}

interface ExportParams {
  level1Accounts: Level1Data[];
  level2Accounts: Level2Data[];
  level3Accounts: Level3Data[];
}

interface FlatRow {
  codigoNivel1: string;
  nomeNivel1: string;
  codigoNivel2: string;
  nomeNivel2: string;
  codigoNivel3: string;
  nomeNivel3: string;
  tipo: string;
}

export function useChartOfAccountsExport() {
  const buildFlatData = (params: ExportParams): FlatRow[] => {
    const { level1Accounts, level2Accounts, level3Accounts } = params;
    const rows: FlatRow[] = [];

    const sortedL1 = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

    sortedL1.forEach(l1 => {
      const childL2 = level2Accounts
        .filter(l2 => l2.level1Id === l1.id)
        .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

      childL2.forEach(l2 => {
        const childL3 = level3Accounts
          .filter(l3 => l3.level2Id === l2.id)
          .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));

        childL3.forEach(l3 => {
          rows.push({
            codigoNivel1: l1.code,
            nomeNivel1: l1.name,
            codigoNivel2: l2.code,
            nomeNivel2: l2.name,
            codigoNivel3: l3.code || '',
            nomeNivel3: l3.name,
            tipo: l3.type,
          });
        });
      });
    });

    return rows;
  };

  const exportToExcel = useCallback(async (params: ExportParams) => {
    const rows = buildFlatData(params);

    const headers = [
      'Código Nível 1',
      'Nome Nível 1',
      'Código Nível 2',
      'Nome Nível 2',
      'Código Nível 3',
      'Nome Nível 3',
      'Tipo',
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Plano de Contas');

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF3B82F6' }
    };
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Add data rows
    rows.forEach(row => {
      worksheet.addRow([
        row.codigoNivel1,
        row.nomeNivel1,
        row.codigoNivel2,
        row.nomeNivel2,
        row.codigoNivel3,
        row.nomeNivel3,
        row.tipo,
      ]);
    });

    // Set column widths
    worksheet.columns = [
      { width: 15 },
      { width: 30 },
      { width: 15 },
      { width: 30 },
      { width: 15 },
      { width: 30 },
      { width: 12 },
    ];

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);

    const now = new Date().toISOString().split('T')[0];
    const filename = `plano-de-contas_${now}.xlsx`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(url);
  }, []);

  const exportToPDF = useCallback((params: ExportParams) => {
    const { level1Accounts, level2Accounts, level3Accounts } = params;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Title
    doc.setFontSize(18);
    doc.text('Plano de Contas', 14, 20);

    // Date
    doc.setFontSize(10);
    const now = new Date().toLocaleDateString('pt-BR');
    doc.text(`Gerado em: ${now}`, 14, 28);

    // Stats
    doc.setFontSize(9);
    doc.text(`Nível 1: ${level1Accounts.length} | Nível 2: ${level2Accounts.length} | Nível 3: ${level3Accounts.length}`, 14, 34);

    // Build hierarchical table data
    const tableData: string[][] = [];
    const sortedL1 = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

    sortedL1.forEach(l1 => {
      // Level 1 row
      tableData.push([`${l1.code} - ${l1.name}`, '', '']);

      const childL2 = level2Accounts
        .filter(l2 => l2.level1Id === l1.id)
        .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

      childL2.forEach(l2 => {
        // Level 2 row
        tableData.push(['', `${l2.code} - ${l2.name}`, '']);

        const childL3 = level3Accounts
          .filter(l3 => l3.level2Id === l2.id)
          .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));

        childL3.forEach(l3 => {
          // Level 3 row with type badge
          const typeBadge = l3.type === 'receita' ? '[R]' : '[D]';
          tableData.push(['', '', `${l3.code} - ${l3.name} ${typeBadge}`]);
        });
      });
    });

    autoTable(doc, {
      head: [['Nível 1', 'Nível 2', 'Nível 3']],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold' },
        1: { cellWidth: 60 },
        2: { cellWidth: 70 },
      },
      didParseCell: (data) => {
        // Style Level 1 rows (first column has content)
        if (data.section === 'body' && data.column.index === 0 && data.cell.text[0]) {
          data.cell.styles.fillColor = [240, 240, 245];
          data.cell.styles.fontStyle = 'bold';
        }
        // Color code types in Level 3
        if (data.section === 'body' && data.column.index === 2 && data.cell.text[0]) {
          const text = data.cell.text[0];
          if (text.includes('[R]')) {
            data.cell.styles.textColor = [22, 163, 74]; // Green for receita
          } else if (text.includes('[D]')) {
            data.cell.styles.textColor = [220, 38, 38]; // Red for despesa
          }
        }
      },
    });

    const now2 = new Date().toISOString().split('T')[0];
    const filename = `plano-de-contas_${now2}.pdf`;
    doc.save(filename);
  }, []);

  const downloadExampleExcel = useCallback(async () => {
    const exampleData = [
      ['1', 'Receitas Operacionais', '1.1', 'Vendas', '1.1.1', 'Vendas de Produtos', 'receita'],
      ['1', 'Receitas Operacionais', '1.1', 'Vendas', '1.1.2', 'Vendas de Serviços', 'receita'],
      ['1', 'Receitas Operacionais', '1.2', 'Outras Receitas', '1.2.1', 'Juros Recebidos', 'receita'],
      ['2', 'Despesas Operacionais', '2.1', 'Custos Fixos', '2.1.1', 'Salários', 'despesa'],
      ['2', 'Despesas Operacionais', '2.1', 'Custos Fixos', '2.1.2', 'Aluguel', 'despesa'],
      ['2', 'Despesas Operacionais', '2.2', 'Custos Variáveis', '2.2.1', 'Comissões', 'despesa'],
      ['2', 'Despesas Operacionais', '2.2', 'Custos Variáveis', '2.2.2', 'Materiais', 'despesa'],
    ];

    const headers = ['Código Nível 1', 'Nome Nível 1', 'Código Nível 2', 'Nome Nível 2', 'Código Nível 3', 'Nome Nível 3', 'Tipo'];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Modelo');

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF3B82F6' }
    };
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Add data rows
    exampleData.forEach(row => {
      worksheet.addRow(row);
    });

    // Set column widths
    worksheet.columns = [
      { width: 15 },
      { width: 25 },
      { width: 15 },
      { width: 20 },
      { width: 15 },
      { width: 25 },
      { width: 12 },
    ];

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo-plano-de-contas.xlsx';
    link.click();
    
    URL.revokeObjectURL(url);
  }, []);

  return { exportToExcel, exportToPDF, downloadExampleExcel };
}
